import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Check, Star, TrendingUp, Users, BarChart3, Shield, Zap, Phone, Mail, ChevronRight, Award, Target, Globe, Clock, Smartphone } from 'lucide-react';

const DealerBenefits = () => {
  const [selectedPlan, setSelectedPlan] = useState('professional');

  const plans = [
    {
      id: 'starter',
      name: 'Starter',
      price: 'R499',
      period: '/month',
      description: 'Perfect for small dealerships getting started',
      features: [
        'Up to 20 vehicle listings',
        'Basic listing with photos',
        'Appear in search results',
        'Monthly performance report',
        'Email support'
      ],
      notIncluded: [
        'Priority listing placement',
        'Lead notifications',
        'Analytics dashboard',
        'Featured dealer badge'
      ],
      color: 'gray',
      popular: false
    },
    {
      id: 'professional',
      name: 'Professional',
      price: 'R1,499',
      period: '/month',
      description: 'Most popular choice for growing dealerships',
      features: [
        'Up to 100 vehicle listings',
        'HD photos & detailed specs',
        'Priority search placement',
        'Real-time lead notifications',
        'Analytics dashboard',
        'Dedicated account manager',
        'Phone & email support',
        'Featured dealer badge'
      ],
      notIncluded: [
        'Homepage spotlight',
        'Social media promotion'
      ],
      color: 'blue',
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 'R3,999',
      period: '/month',
      description: 'For large dealerships & dealer groups',
      features: [
        'Unlimited vehicle listings',
        'HD photos, videos & 360° views',
        'Top search placement',
        'Real-time lead notifications',
        'Advanced analytics & insights',
        'API integration support',
        'Homepage spotlight rotation',
        'Social media promotion',
        'Priority phone support',
        'Custom branding options',
        'Multi-branch management'
      ],
      notIncluded: [],
      color: 'purple',
      popular: false
    }
  ];

  const stats = [
    { value: '50,000+', label: 'Monthly Visitors', icon: Users },
    { value: '5,000+', label: 'Cars Listed', icon: Building2 },
    { value: '10,000+', label: 'Leads Generated', icon: Target },
    { value: '173+', label: 'Partner Dealers', icon: Award }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Increase Your Sales',
      description: 'Reach thousands of active car buyers searching for vehicles in South Africa every day.'
    },
    {
      icon: Target,
      title: 'Qualified Leads',
      description: 'Receive leads from buyers actively searching for vehicles matching your inventory.'
    },
    {
      icon: BarChart3,
      title: 'Performance Analytics',
      description: 'Track views, enquiries, and conversions with our comprehensive analytics dashboard.'
    },
    {
      icon: Globe,
      title: 'Nationwide Reach',
      description: 'Expand beyond your local area. Reach buyers across all 9 provinces of South Africa.'
    },
    {
      icon: Smartphone,
      title: 'Mobile Optimized',
      description: 'Your listings look perfect on any device. 70% of our traffic comes from mobile.'
    },
    {
      icon: Shield,
      title: 'Trusted Platform',
      description: 'Join a network of verified dealerships trusted by thousands of SA car buyers.'
    }
  ];

  const testimonials = [
    {
      quote: "Since joining AutoSearch SA, we've seen a 40% increase in qualified leads. The platform is easy to use and the support team is excellent.",
      name: "Johan van der Merwe",
      role: "Owner, JVM Auto Sales",
      location: "Pretoria"
    },
    {
      quote: "The analytics dashboard helps us understand what buyers are looking for. We've optimized our inventory based on the insights.",
      name: "Sarah Nkosi",
      role: "Sales Manager, Nkosi Motors",
      location: "Johannesburg"
    },
    {
      quote: "Best investment we've made for our dealership. The ROI has been incredible.",
      name: "David Botha",
      role: "Director, Botha Auto Group",
      location: "Cape Town"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1562141961-b5d1909d6de9?w=1920&q=80')] bg-cover bg-center opacity-10"></div>
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm mb-6">
              <Zap className="w-4 h-4 text-yellow-400" />
              <span>Join 173+ Successful Dealerships</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Grow Your Dealership with AutoSearch SA
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Reach thousands of qualified car buyers. List your vehicles on South Africa's fastest-growing automotive marketplace.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/dealer-portal" 
                className="px-8 py-4 bg-white text-blue-900 rounded-xl font-bold hover:bg-blue-50 transition flex items-center justify-center gap-2"
              >
                <Building2 className="w-5 h-5" />
                Register Your Dealership
              </Link>
              <a 
                href="#pricing" 
                className="px-8 py-4 bg-white/10 border border-white/30 rounded-xl font-bold hover:bg-white/20 transition"
              >
                View Pricing
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center">
                <stat.icon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                <p className="text-gray-500 dark:text-gray-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Why Dealers Choose Us</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              AutoSearch SA provides everything you need to sell more cars and grow your business.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm hover:shadow-lg transition">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{benefit.title}</h3>
                <p className="text-gray-600 dark:text-gray-400">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 bg-gray-100 dark:bg-gray-800">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Simple, Transparent Pricing</h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Choose the plan that fits your dealership. No hidden fees. Cancel anytime.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan) => (
              <div 
                key={plan.id}
                className={`relative bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-sm ${
                  plan.popular ? 'ring-2 ring-blue-600 shadow-lg' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-blue-600 text-white text-sm font-bold rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">{plan.price}</span>
                    <span className="text-gray-500">{plan.period}</span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">{plan.description}</p>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 dark:text-gray-300 text-sm">{feature}</span>
                    </li>
                  ))}
                  {plan.notIncluded.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 opacity-50">
                      <span className="w-5 h-5 flex items-center justify-center text-gray-400">—</span>
                      <span className="text-gray-500 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/dealer-portal"
                  className={`w-full py-3 rounded-xl font-bold text-center block transition ${
                    plan.popular
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  Get Started
                </Link>
              </div>
            ))}
          </div>

          <p className="text-center text-gray-500 dark:text-gray-400 mt-8">
            All prices exclude VAT. Need a custom solution? <a href="#contact" className="text-blue-600 hover:underline">Contact us</a>
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">What Dealers Say</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-4 italic">"{testimonial.quote}"</p>
                <div>
                  <p className="font-bold text-gray-900 dark:text-white">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.role}</p>
                  <p className="text-sm text-gray-500">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-100 dark:bg-gray-800">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Get Started in 3 Easy Steps</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Register</h3>
              <p className="text-gray-600 dark:text-gray-400">Create your dealer account and choose a plan that fits your needs.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">List Your Cars</h3>
              <p className="text-gray-600 dark:text-gray-400">Upload your inventory with photos, specs, and pricing. It takes minutes.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Get Leads</h3>
              <p className="text-gray-600 dark:text-gray-400">Start receiving enquiries from qualified buyers across South Africa.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="py-16 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Grow Your Dealership?</h2>
            <p className="text-blue-100 mb-8">
              Join hundreds of successful dealerships already selling on AutoSearch SA.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link 
                to="/dealer-portal" 
                className="px-8 py-4 bg-white text-blue-600 rounded-xl font-bold hover:bg-blue-50 transition flex items-center justify-center gap-2"
              >
                Register Now
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center text-blue-100">
              <a href="tel:+27123456789" className="flex items-center justify-center gap-2 hover:text-white">
                <Phone className="w-5 h-5" />
                +27 12 345 6789
              </a>
              <a href="mailto:dealers@autosearchsa.co.za" className="flex items-center justify-center gap-2 hover:text-white">
                <Mail className="w-5 h-5" />
                dealers@autosearchsa.co.za
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              {[
                {
                  q: "How long does it take to get started?",
                  a: "You can create an account and start listing vehicles in under 10 minutes. Our team will verify your dealership within 24 hours."
                },
                {
                  q: "Can I upgrade or downgrade my plan?",
                  a: "Yes, you can change your plan at any time. Changes take effect at the start of your next billing cycle."
                },
                {
                  q: "What payment methods do you accept?",
                  a: "We accept all major credit cards, EFT, and debit orders for monthly subscriptions."
                },
                {
                  q: "Is there a contract or commitment?",
                  a: "No long-term contracts. All plans are month-to-month and you can cancel anytime."
                },
                {
                  q: "How do I receive leads?",
                  a: "Leads are sent directly to your email and mobile (SMS). You can also view all leads in your dealer dashboard."
                }
              ].map((faq, idx) => (
                <div key={idx} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2">{faq.q}</h3>
                  <p className="text-gray-600 dark:text-gray-400">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DealerBenefits;
